import { createSlice } from '@reduxjs/toolkit';

const normalizePromoBackgroundImage = (value) => {
  if (!value || typeof value !== 'string') return null;

  const trimmedValue = value.trim();

  if (!trimmedValue || trimmedValue.startsWith('#')) return null;

  return trimmedValue;
};

const initialState = {
  isLoading: false,
  isActive: false,
  id: 0,
  uid: 0,
  sid: 0,

  blockContent: {
    cta: [],
    message: '',
    subtitle: '',
    isDismissible: false,
    iconUrl: null,
    timerEndsAt: null,
    timerColor: null,
    textAnimation: 'none',
  },
  blockDesign: {
    textColor: '#ffffff',
    padding: 0,
    textAnimation: 'none',
    reelMotion: 'settle',
    reelHeight: 32,
    reelPause: 1,
    reelFill: false,
    separatorSymbol: '',
    motifSymbol: '',
    fadeEdges: true,
    textSize: '14',
    view: 'top',
    buttonLayout: 'auto',
    titleBold: false,
    contentAlign: 'left',
    buttonHeight: 'regular',
    iconHeight: 44,
    textBackground: '#ffffff',
    textBackgroundOpacity: 1,
    textBackgroundPadding: 10,
    textBackgroundBorderRadius: 6,

    containerDesign: {
      backdropBlur: 0,
      backdropBrightness: 0,
      backdropContrast: 0,
      backgroundAnimation: null,
      backgroundImage: null,
      backgroundStyle: null,
      backgroundType: 1,
      border: 'soft',
      borderColor: null,
      textColor: '#ffffff',
      backgroundColor: '#0082FE',
      gradientColor1: '#0082FE',
      gradientColor2: '#ffffff',
      gradientDirection: 0,
      gradientOffset: 0,
      isBackground: true,
      isFullWidth: false,
      isSectionMinHeight: null,
      margin: 15,
      padding: 12,
      opacity: 0,
    },
  },
  blockSettings: {
    isMaster: null,
  },
};

export const promoSlice = createSlice({
  name: 'promo',
  initialState,
  reducers: {
    promoStore: (state, action) => {
      const { id, isActive, sid, uid, data } = action.payload;

      const block = data ?? {};
      const blockContent = block.blockContent ?? {};
      const blockDesign = block.blockDesign ?? initialState.blockDesign;
      const containerDesign = blockDesign?.containerDesign ?? initialState.blockDesign.containerDesign;
      const blockSettings = block.blockSettings ?? {};

      state.isLoading = false;
      state.id = id ?? 0;
      state.isActive = isActive;
      state.sid = sid;
      state.uid = uid;

      state.blockContent = {
        message: blockContent.message ?? '',
        subtitle: blockContent.subtitle ?? '',
        isDismissible: blockContent.isDismissible ?? false,
        iconUrl: blockContent.iconUrl ?? null,
        timerEndsAt: blockContent.timerEndsAt ?? null,
        timerColor: blockContent.timerColor ?? null,
        textAnimation: blockContent.textAnimation ?? 'none',
        cta: blockContent.cta ?? [],
      };

      state.blockDesign = {
        textColor: blockDesign.textColor ?? containerDesign.textColor ?? '#000000',
        textAnimation: blockDesign.textAnimation ?? 'none',
        reelMotion: blockDesign.reelMotion ?? 'settle',
        reelHeight: blockDesign.reelHeight ?? 32,
        reelPause: blockDesign.reelPause ?? 1,
        reelFill: blockDesign.reelFill ?? false,
        separatorSymbol: blockDesign.separatorSymbol ?? '',
        motifSymbol: blockDesign.motifSymbol ?? '',
        fadeEdges: blockDesign.fadeEdges ?? true,
        textSize: blockDesign.textSize ?? '14',
        padding: containerDesign.padding ?? blockDesign.padding ?? 12,
        view: blockDesign.view ?? 'top',
        buttonLayout: blockDesign.buttonLayout ?? 'auto',
        titleBold: blockDesign.titleBold ?? false,
        contentAlign: blockDesign.contentAlign ?? 'left',
        buttonHeight: blockDesign.buttonHeight ?? 'regular',
        iconHeight: blockDesign.iconHeight ?? 44,
        textBackground: blockDesign.textBackground ?? '#ffffff',
        textBackgroundOpacity: blockDesign.textBackgroundOpacity ?? 1,
        textBackgroundPadding: blockDesign.textBackgroundPadding ?? 10,
        textBackgroundBorderRadius: blockDesign.textBackgroundBorderRadius ?? 6,

        containerDesign: {
          backdropBlur: containerDesign.backdropBlur ?? 0,
          backdropBrightness: containerDesign.backdropBrightness ?? 0,
          backdropContrast: containerDesign.backdropContrast ?? 0,

          backgroundAnimation: containerDesign.backgroundAnimation ?? null,
          backgroundStyle: containerDesign.backgroundStyle ?? 1,
          backgroundColor: blockDesign.backgroundColor ?? containerDesign.backgroundColor ?? '#0082FE',

          backgroundType: containerDesign.backgroundType ?? 1,
          backgroundImage: normalizePromoBackgroundImage(containerDesign.backgroundImage),

          gradientColor1: containerDesign.gradientColor1 ?? '#0082FE',
          gradientColor2: containerDesign.gradientColor2 ?? '#ffffff',
          gradientDirection: containerDesign.gradientDirection ?? 0,
          gradientOffset: containerDesign.gradientOffset ?? 0,

          border: containerDesign.border ?? 'soft',
          borderColor: containerDesign.borderColor ?? '#ffffff',
          textColor: containerDesign.textColor ?? blockDesign.textColor ?? '#000000',

          isBackground: containerDesign.isBackground ?? true,
          isFullWidth: containerDesign.isFullWidth ?? false,
          isSectionMinHeight: containerDesign.isSectionMinHeight ?? null,

          margin: containerDesign.margin ?? 15,
          padding: containerDesign.padding ?? blockDesign.padding ?? 12,
          opacity: containerDesign.opacity ?? 0,
        },
      };
      state.blockSettings = {
        isMaster: blockSettings.isMaster ?? null,
      };
    },
    promoClear: () => initialState,

    promoUpdateIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    promoUpdateIsActive: (state, action) => {
      state.isActive = action.payload;
    },
    promoUpdateBlockContent: (state, action) => {
      state.blockContent = action.payload;
    },
    promoUpdateBlockDesign: (state, action) => {
      state.blockDesign = action.payload;
    },
    promoUpdateBlockSettings: (state, action) => {
      state.blockSettings = action.payload;
    },
  },
});

export const { promoStore, promoClear, promoUpdateIsLoading, promoUpdateIsActive, promoUpdateBlockContent, promoUpdateBlockDesign, promoUpdateBlockSettings } = promoSlice.actions;

export const selectPromoIsLoading = (state) => state.promo.isLoading;

export const selectPromoId = (state) => state.promo.id;
export const selectPromoIsActive = (state) => state.promo.isActive;
export const selectPromoIsAdded = (state) => Boolean(state.promo.id);
export const selectPromoUID = (state) => state.promo.uid;
export const selectPromoContainerDesign = (state) => state.promo.blockDesign.containerDesign;
export const selectPromoBlockContent = (state) => state.promo.blockContent;
export const selectPromoBlockDesign = (state) => state.promo.blockDesign;

export const selectPromoBlockSettings = (state) => state.promo.blockSettings;

export default promoSlice.reducer;
